import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { shallow } from 'zustand/shallow'
import { Radio, RadioGroup, FormControlLabel } from '@mui/material'
import { useNetwork, useAccount } from 'wagmi'

import { useChainAndNetwork, networkType } from 'src/store/chainAndNetwork'
import { useAuthState, useAuthDispatch } from 'src/contexts/authContext'
import { evmNetworkIds, evmNetworkIdsType } from 'src/util/chainIds'

const NetworkOptions = () => {
  const {
    chain: selectedChain,
    network: selectedNetwork,
    setNetwork,
  } = useChainAndNetwork(
    (state) => ({
      chain: state.chain,
      network: state.network,
      setNetwork: state.setNetwork,
    }),
    shallow,
  )

  const dispatchAction = useAuthDispatch()
  const areOptionsDisabled = useAuthState().disableNetworkOptions

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const queryNetwork = queryParams.get('network')
  const urlPathName = location.pathname

  const { chain } = useNetwork()
  const walletConnectAccount = useAccount()

  type testnetNamesType = {
    bnb: 'Sepolia'
    ethereum: 'Sepolia'
    polygon: 'Mumbai'
    multiversx: 'Devnet'
  }

  const testnetNames = {
    bnb: '',
    ethereum: ' (Sepolia)',
    polygon: ' (Mumbai)',
    multiversx: ' (Devnet)',
  }

  const handleSelectNetwork = (network: networkType) => {
    // Restrict airdrop page to only devnet
    if (urlPathName.includes('/airdrop') && network !== 'testnet') {
      return
    }

    // Restrict evm to only testnets
    if (selectedChain !== 'multiversx' || areOptionsDisabled) {
      return
    }

    setNetwork(network)

    // const metamaskChainIdMismatch =
    //   adapter.metamask?.connector?.isConnected() &&
    //   adapter.metamask?.connector?.selectedAddress &&
    //   adapter.metamask?.connector?._state?.isUnlocked &&
    //   parseInt(adapter.metamask?.connector?.networkVersion) !==
    //     evmNetworkIds[selectedChain as keyof evmNetworkIdsType][network];

    const walletConnectChainIdMismatch =
      walletConnectAccount?.isConnected &&
      chain?.id !== evmNetworkIds[selectedChain.toUpperCase() as keyof evmNetworkIdsType][network]

    if (selectedChain !== 'multiversx' && walletConnectChainIdMismatch) {
      dispatchAction({
        type: 'SWITCHNETWORKMODAL',
        payload: true,
      })
    }
  }

  useEffect(() => {
    if (
      (queryNetwork === 'mainnet' || queryNetwork === 'testnet') &&
      queryNetwork !== selectedNetwork
    ) {
      handleSelectNetwork(queryNetwork)
    }
  }, [queryNetwork])

  useEffect(() => {
    if (
      urlPathName.includes('/airdrop') ||
      (urlPathName.includes('/create-smart-contract') && selectedNetwork !== 'testnet')
    ) {
      handleSelectNetwork('testnet')
    }
  }, [urlPathName])

  return (
    <RadioGroup
      aria-labelledby="demo-controlled-radio-buttons-group"
      name="controlled-radio-buttons-group"
      value={selectedNetwork}
      onChange={(e) => handleSelectNetwork(e.target.value as networkType)}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        margin: '15px 0',

        '& .MuiFormControlLabel-label': {
          fontSize: '16px',
          fontWeight: '600',
          color: '#ffffff',
          fontFamily: 'SpaceGrotesk, sans-serif',

          '&.MuiTypography-root': {
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.5)',
            },
          },
        },

        '& .MuiButtonBase-root': {
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.5)',
          },
        },

        '& .MuiRadio-root': {
          color: '#ffffff',

          '&.Mui-checked': {
            color: '#2AEAFA',
          },
        },
      }}>
      <FormControlLabel
        value="mainnet"
        control={<Radio />}
        label="Mainnet"
        disabled={
          selectedChain !== 'multiversx' || areOptionsDisabled || urlPathName.includes('/airdrop')
        }
      />
      <FormControlLabel
        value="testnet"
        control={<Radio />}
        label={`Testnet${testnetNames[selectedChain as keyof testnetNamesType]}`}
        disabled={areOptionsDisabled}
      />
    </RadioGroup>
  )
}

export default NetworkOptions
